// TEMPORÁRIO — mock local do Portal do Credenciado, remover no desenvolvimento do backend.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { diaDaqui, resolverMock } from '@/modulos/portal-credenciado/comum/mock';

import type { ColaboradorDoCredenciado, OperadorDelegado, RepresentanteLegal } from './contrato';
import dadosMock from './dados.mock.json';

/**
 * Camada de dados da equipe do credenciado.
 *
 * Mesmo desenho das demais telas do módulo: nenhum componente busca dado
 * direto, a chave de cache mora junto de quem a usa. A diferença para o
 * padrão real (ex. `operacao/condutores/dados.ts`) é o `queryFn`, que resolve
 * dado local em vez de chamar `chamarApi(...)` — ver a justificativa em
 * `comum/mock.ts`.
 *
 * ESTE MÓDULO É SÓ LEITURA. CLAUDE.md, seção "Proibido", só autoriza mock de
 * leitura: adicionar, editar, bloquear e reativar colaborador mostram o aviso
 * de indisponibilidade na tela (`EquipePage.tsx`, `EquipeFormulario.tsx`) e
 * não mexem nesta lista, nem em memória. Voltam a gravar quando existir a API.
 */

const COLABORADORES = dadosMock.colaboradores as ColaboradorDoCredenciado[];

export const CHAVES_DE_EQUIPE = {
  lista: ['credenciado', 'equipe', 'lista'] as const,
  operadoresDelegados: ['credenciado', 'equipe', 'operadores-delegados'] as const,
  representantesLegais: ['credenciado', 'equipe', 'representantes-legais'] as const,
};

export function useColaboradores(): UseQueryResult<ColaboradorDoCredenciado[], Error> {
  return useQuery<ColaboradorDoCredenciado[], Error>({
    queryKey: CHAVES_DE_EQUIPE.lista,
    queryFn: () => resolverMock(COLABORADORES),
  });
}

/**
 * Operadores delegados e representantes legais: SÓ LEITURA, como a lista de
 * colaboradores acima. Convidar, revogar, auditoria e "Solicitar
 * alteração" mostram o aviso de indisponibilidade na tela e não mexem neste
 * array, nem em memória.
 */
const OPERADORES_DELEGADOS = dadosMock.operadores_delegados as OperadorDelegado[];

export function useOperadoresDelegados(): UseQueryResult<OperadorDelegado[], Error> {
  return useQuery<OperadorDelegado[], Error>({
    queryKey: CHAVES_DE_EQUIPE.operadoresDelegados,
    queryFn: () => resolverMock(OPERADORES_DELEGADOS),
  });
}

/**
 * NENHUMA DATA LITERAL NO MOCK. O arquivo guarda a distância em dias até hoje
 * (`vigencia_desde_em_dias`, negativo é passado), para a vigência não
 * envelhecer sozinha — mesmo desenho de `veiculos/dados.ts`.
 */
type RepresentanteDoMock = Omit<RepresentanteLegal, 'vigencia_desde'> & {
  vigencia_desde_em_dias: number;
};

export function montarRepresentantes(hoje: Date = new Date()): RepresentanteLegal[] {
  return (dadosMock.representantes_legais as RepresentanteDoMock[]).map(
    ({ vigencia_desde_em_dias, ...representante }) => ({
      ...representante,
      vigencia_desde: diaDaqui(vigencia_desde_em_dias, hoje),
    }),
  );
}

export function useRepresentantesLegais(): UseQueryResult<RepresentanteLegal[], Error> {
  return useQuery<RepresentanteLegal[], Error>({
    queryKey: CHAVES_DE_EQUIPE.representantesLegais,
    queryFn: () => resolverMock(montarRepresentantes()),
  });
}
